/**
 * ARRIVING FROM THE LOGIN, ALREADY ALLOWED IN.
 *
 * The login lives on lightsos.com and cannot hand the app anything except
 * through the URL it sends you to. It puts the access code in the FRAGMENT,
 * after the hash, rather than in a query string, because a fragment is never
 * transmitted to a server: it does not appear in Netlify's logs, in a proxy's
 * logs, or in a Referer header on the way to anywhere else.
 *
 * That is the only property the fragment buys. It is still visible in the
 * address bar and it still lands in browser history, which is why the caller
 * strips it the moment it has been read.
 *
 * What makes the code secret is on the other side: a function on the website
 * verifies the password against an environment variable and only then returns
 * the code, so the code never ships inside a page anyone can View Source on.
 * This file is the receiving half of that arrangement and nothing more.
 */

/**
 * The access code carried in a location hash, or null.
 *
 * Deliberately tolerant about shape and strict about content. A hash may
 * arrive as #k=CODE, or with other parameters around it, or percent encoded,
 * or with a stray trailing slash from a redirect chain. Any of those should
 * work. Anything that is not a plausible code is refused rather than saved,
 * because a saved wrong code is the failure that looks exactly like a broken
 * feature: the app stops asking and starts being told no.
 */
export function codeFromHash(hash: string | undefined | null): string | null {
  if (!hash) return null
  const raw = hash.startsWith('#') ? hash.slice(1) : hash

  let found: string | null = null
  for (const part of raw.split('&')) {
    const eq = part.indexOf('=')
    if (eq < 0) continue
    if (part.slice(0, eq) !== 'k') continue
    found = part.slice(eq + 1)
    break
  }
  if (!found) return null

  let code: string
  try {
    code = decodeURIComponent(found)
  } catch {
    // A malformed escape is not a code.
    return null
  }
  code = code.trim().replace(/\/+$/, '')

  /*
   * A length and character floor, not a format. The server decides what is
   * valid; this only refuses the things that could never be right - an empty
   * value, a fragment that was really a route, a code with whitespace in it -
   * so they are not stored and then blamed on the login.
   */
  if (code.length < 8 || code.length > 512) return null
  if (/[\s#?]/.test(code)) return null
  return code
}
